import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { TimelineItem, TicketAttachment } from '../types';
import { getStatusLabel } from '../types';
import type { TicketStatus } from '../types';
import { Send, X, Download, FileText, Archive, Mic, Camera, Image, Plus, CheckCheck } from 'lucide-react';
import VoiceRecorder from './VoiceRecorder';
import { format, isToday, isYesterday } from 'date-fns';
import { he } from 'date-fns/locale';

function isImageMime(mime: string): boolean {
  return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mime);
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatMessageTime(dateStr: string): string {
  const d = new Date(dateStr);
  return format(d, 'HH:mm', { locale: he });
}

function formatDateSeparator(dateStr: string, t: (k: string) => string): string {
  const d = new Date(dateStr);
  if (isToday(d)) return t('chat.today');
  if (isYesterday(d)) return t('chat.yesterday');
  return format(d, 'EEEE, d בMMMM yyyy', { locale: he });
}

function shouldShowDateSeparator(current: string, previous: string | null): boolean {
  if (!previous) return true;
  const c = new Date(current).toDateString();
  const p = new Date(previous).toDateString();
  return c !== p;
}

function AttachmentBubble({ att, isOwn }: { att: TicketAttachment; isOwn: boolean }) {
  if (isImageMime(att.mime_type)) {
    return (
      <a href={att.url} target="_blank" rel="noreferrer" className="block">
        <img
          src={att.url}
          alt={att.original_name}
          className="max-w-[260px] max-h-[200px] rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a
      href={att.url}
      download={att.original_name}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-colors border ${
        isOwn
          ? 'bg-surface-0/10 border-white/20 hover:bg-surface-0/20 text-white'
          : 'bg-surface-50 border-surface-400/15 hover:bg-surface-100 text-surface-700'
      }`}
    >
      {att.mime_type === 'application/pdf' ? <FileText size={14} /> :
        att.mime_type.includes('zip') ? <Archive size={14} /> : <FileText size={14} />}
      <span className="max-w-[150px] truncate">{att.original_name}</span>
      <span className="opacity-60">{fmtBytes(att.file_size_bytes)}</span>
      <Download size={12} />
    </a>
  );
}

function PendingImagePreview({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [url, setUrl] = useState<string>('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  if (!isImageFile(file)) {
    return (
      <div className="relative group">
        <div className="w-16 h-16 rounded-xl bg-surface-100 flex items-center justify-center border border-surface-400/15">
          <FileText size={20} className="text-surface-400" />
        </div>
        <button onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-danger-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-card">
          <X size={10} />
        </button>
        <p className="text-[10px] text-surface-500 mt-0.5 max-w-[64px] truncate text-center">{file.name}</p>
      </div>
    );
  }

  return (
    <div className="relative group">
      <img src={url} alt={file.name}
        className="w-16 h-16 rounded-xl object-cover border border-surface-400/15 shadow-card" />
      <button onClick={onRemove}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-danger-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-card">
        <X size={10} />
      </button>
    </div>
  );
}

interface Props {
  timeline: TimelineItem[];
  ticketId: string;
}

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

export default function TicketChatView({ timeline, ticketId }: Props) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [content, setContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [timeline.length, scrollToBottom]);

  const autoResizeTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, []);

  const submit = async () => {
    const hasContent = content.trim().length > 0;
    const hasFiles = pendingFiles.length > 0;
    if (!hasContent && !hasFiles) return;

    setSubmitting(true);
    setError('');
    try {
      const text = hasContent ? content.trim() : (hasFiles ? '📎' : '');
      const commentRes = await api.createComment(ticketId, { content: text, is_internal: false });
      const commentId = commentRes.data?.id;
      if (commentId && hasFiles) {
        let uploadFailed = false;
        for (const file of pendingFiles) {
          try { await api.uploadCommentAttachment(ticketId, commentId, file); } catch { uploadFailed = true; }
        }
        if (uploadFailed) {
          setError(t('chat.uploadError'));
        }
      }
      setContent('');
      setPendingFiles([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      scrollToBottom();
    } catch {
      setError(t('chat.sendError'));
    } finally {
      setSubmitting(false);
    }
  };

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const all = Array.from(files);
    const valid = all.filter((f) => f.size <= 10 * 1024 * 1024);
    if (valid.length < all.length) {
      setError(t('chat.fileTooLarge'));
    }
    setPendingFiles((prev) => [...prev, ...valid]);
    setShowAttachMenu(false);
  };

  const chatItems = timeline.filter((item) => {
    if (item.item_type === 'comment') return !item.is_deleted;
    if (item.item_type === 'event' && item.event_type === 'status_changed') return true;
    return false;
  });

  return (
    <div className="flex flex-col h-full bg-[#efeae2]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'40\' height=\'40\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'1\' fill=\'%23d5cfca\' opacity=\'0.4\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'200\' height=\'200\' fill=\'url(%23p)\'/%3E%3C/svg%3E")' }}>
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 md:px-4 py-4 min-h-[300px] max-h-[60vh]">
        {chatItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-surface-0/80 rounded-full flex items-center justify-center mb-3 shadow-card">
              <Send size={24} className="text-surface-400" />
            </div>
            <p className="text-surface-500 text-sm">{t('chat.noMessages')}</p>
            <p className="text-surface-400 text-xs mt-1">{t('chat.startConversation')}</p>
          </div>
        )}
        {chatItems.map((item, idx) => {
          const prevItem = idx > 0 ? chatItems[idx - 1] : null;
          const showDateSep = shouldShowDateSeparator(item.created_at, prevItem?.created_at ?? null);

          if (item.item_type === 'event' && item.event_type === 'status_changed') {
            const from = item.metadata?.from ? getStatusLabel(item.metadata.from as TicketStatus, t) : '';
            const to = item.metadata?.to ? getStatusLabel(item.metadata.to as TicketStatus, t) : '';
            return (
              <div key={item.id}>
                {showDateSep && (
                  <div className="flex justify-center my-3">
                    <span className="text-[11px] text-surface-500 bg-surface-0/90 px-4 py-1 rounded-full shadow-card">
                      {formatDateSeparator(item.created_at, t)}
                    </span>
                  </div>
                )}
                <div className="flex justify-center my-2">
                  <span className="text-[11px] text-surface-500 bg-[#fdf4c1] px-4 py-1.5 rounded-lg shadow-card">
                    {t('chat.statusChanged')} {from} → {to}
                  </span>
                </div>
              </div>
            );
          }

          const isOwnMessage = item.actor_id === user?.id;
          const prevSameAuthor = prevItem?.actor_id === item.actor_id && prevItem?.item_type === 'comment';
          const hasAttachments = item.attachments && item.attachments.length > 0;
          const isImageOnly = hasAttachments && item.attachments!.every(a => isImageMime(a.mime_type)) && (!item.content || item.content === '📎');

          return (
            <div key={item.id}>
              {showDateSep && (
                <div className="flex justify-center my-3">
                  <span className="text-[11px] text-surface-500 bg-surface-0/90 px-4 py-1 rounded-full shadow-card">
                    {formatDateSeparator(item.created_at, t)}
                  </span>
                </div>
              )}
              <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${prevSameAuthor ? 'mt-0.5' : 'mt-3'}`}>
                <div className={`max-w-[80%] md:max-w-[65%]`}>
                  {!prevSameAuthor && (
                    <div className={`flex items-center gap-2 mb-0.5 px-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      <span className={`text-[11px] font-semibold ${isOwnMessage ? 'text-teal-700' : 'text-indigo-700'}`}>
                        {isOwnMessage ? t('chat.you') : item.actor_name}
                      </span>
                    </div>
                  )}
                  {isImageOnly ? (
                    <div className={`rounded-xl overflow-hidden shadow-card ${isOwnMessage ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}>
                      {item.attachments!.map((att) => (
                        <a key={att.id} href={att.url} target="_blank" rel="noreferrer" className="block">
                          <img src={att.url} alt={att.original_name}
                            className="max-w-[280px] max-h-[240px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            loading="lazy" />
                        </a>
                      ))}
                      <div className={`px-2 py-1 text-[10px] ${isOwnMessage ? 'bg-[#d9fdd3] text-surface-500 text-left' : 'bg-surface-0 text-surface-400 text-left'} flex items-center gap-1`}>
                        <span>{formatMessageTime(item.created_at)}</span>
                        {isOwnMessage && <CheckCheck size={12} className="text-info-500" />}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`rounded-xl px-3 py-2 text-sm leading-relaxed shadow-card relative ${
                        isOwnMessage
                          ? 'bg-[#d9fdd3] text-surface-800 rounded-tr-sm'
                          : 'bg-surface-0 text-surface-800 rounded-tl-sm'
                      }`}
                    >
                      {item.content && item.content !== '📎' && (
                        <p className="whitespace-pre-wrap break-words">{item.content}</p>
                      )}
                      {hasAttachments && (
                        <div className="mt-1.5 space-y-1.5">
                          {item.attachments!.map((att) => (
                            <AttachmentBubble key={att.id} att={att} isOwn={isOwnMessage} />
                          ))}
                        </div>
                      )}
                      <div className={`flex items-center gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-[10px] text-surface-400">{formatMessageTime(item.created_at)}</span>
                        {isOwnMessage && <CheckCheck size={12} className="text-info-500" />}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="bg-[#f0f0f0] border-t border-surface-400/15 px-2 md:px-3 py-2">
        {/* Pending files preview */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 p-2 bg-surface-0 rounded-xl shadow-card">
            {pendingFiles.map((f, i) => (
              <PendingImagePreview key={i} file={f} onRemove={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-danger-50 rounded-lg">
            <X size={14} className="text-danger-500" />
            <p className="text-xs text-danger-600">{error}</p>
          </div>
        )}

        <div className="flex items-end gap-1.5">
          {/* Attach menu */}
          <div className="relative">
            <button
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              className="p-2.5 text-surface-500 hover:text-primary-600 transition-colors rounded-full hover:bg-surface-0/80"
              aria-label={t('chat.attachFile')}
            >
              <Plus size={22} className={`transition-transform ${showAttachMenu ? 'rotate-45' : ''}`} />
            </button>
            {showAttachMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowAttachMenu(false)} />
                <div className="absolute bottom-12 right-0 modal-content border border-surface-400/10 py-2 z-20 min-w-[180px] animate-in slide-in-from-bottom-2">
                  <button onClick={() => { galleryRef.current?.click(); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-surface-700 hover:bg-surface-50 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                      <Image size={18} className="text-purple-600" />
                    </div>
                    <span>{t('chat.gallery')}</span>
                  </button>
                  <button onClick={() => { cameraRef.current?.click(); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-surface-700 hover:bg-surface-50 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-pink-100 flex items-center justify-center">
                      <Camera size={18} className="text-pink-600" />
                    </div>
                    <span>{t('chat.camera')}</span>
                  </button>
                  <button onClick={() => { fileRef.current?.click(); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-sm text-surface-700 hover:bg-surface-50 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-info-100 flex items-center justify-center">
                      <FileText size={18} className="text-info-600" />
                    </div>
                    <span>{t('chat.document')}</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <input ref={galleryRef} type="file" accept={IMAGE_ACCEPT} multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />

          {showVoiceRecorder ? (
            <div className="flex-1">
              <VoiceRecorder
                onRecordingComplete={(file) => {
                  setShowVoiceRecorder(false);
                  setPendingFiles((prev) => [...prev, file]);
                }}
                onCancel={() => setShowVoiceRecorder(false)}
              />
            </div>
          ) : (
            <>
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  className="w-full resize-none bg-surface-0 border-0 rounded-2xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 min-h-[44px] max-h-[120px] shadow-card"
                  placeholder={t('chat.typeMessage')}
                  value={content}
                  onChange={(e) => { setContent(e.target.value); autoResizeTextarea(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
                  rows={1}
                  dir="auto"
                />
              </div>

              <button
                onClick={() => setShowVoiceRecorder(true)}
                className="p-2.5 text-surface-500 hover:text-primary-600 transition-colors rounded-full hover:bg-surface-0/80 flex-shrink-0"
                aria-label={t('voice.record')}
              >
                <Mic size={22} />
              </button>
            </>
          )}

          <button
            onClick={submit}
            disabled={submitting || (!content.trim() && pendingFiles.length === 0)}
            className="p-2.5 bg-[#00a884] text-white rounded-full hover:bg-[#008f72] disabled:opacity-40 transition-all flex-shrink-0 shadow-card active:scale-95"
            aria-label={t('chat.sendMessage')}
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
